import React, { useEffect, useState } from 'react'

import GradientButton from '../../components/GradientButton'
import { useT } from '../../i18n'
import { readHarvestTargetWeightG, readProjectionTgc } from '../../utils/lotProjectionSettings'

// Lot projections (findings #82): facility defaults — the harvest target a
// lot uses when it has none, and the TGC used when a lot has only one
// sampling. Kept in `Farm.metadata` (no migration).

export type FacilityProjectionSettingsProps = {
  farm: { id: string; metadata?: Record<string, unknown> }
  disabled: boolean
  onSave: (settings: { harvestTargetWeightG: number | null; projectionTgc: number | null }) => Promise<void>
}

const parse = (value: string): number | null | 'invalid' => {
  const trimmed = value.trim().replace(',', '.')
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 'invalid'
}

export default function FacilityProjectionSettings({ farm, disabled, onSave }: FacilityProjectionSettingsProps) {
  const t = useT()
  const savedTarget = readHarvestTargetWeightG(farm)
  const savedTgc = readProjectionTgc(farm)
  const [target, setTarget] = useState(savedTarget === null ? '' : String(savedTarget))
  const [tgc, setTgc] = useState(savedTgc === null ? '' : String(savedTgc))
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setTarget(savedTarget === null ? '' : String(savedTarget))
    setTgc(savedTgc === null ? '' : String(savedTgc))
    setError(null)
  }, [farm.id, savedTarget, savedTgc])

  const dirty = target.trim() !== (savedTarget === null ? '' : String(savedTarget))
    || tgc.trim() !== (savedTgc === null ? '' : String(savedTgc))

  const save = async () => {
    const nextTarget = parse(target)
    const nextTgc = parse(tgc)
    if (nextTarget === 'invalid' || nextTgc === 'invalid' || (typeof nextTgc === 'number' && nextTgc > 6)) {
      setError(t('editFarm.projection.invalid'))
      return
    }
    setError(null)
    setSaving(true)
    try {
      await onSave({ harvestTargetWeightG: nextTarget, projectionTgc: nextTgc })
    } finally {
      setSaving(false)
    }
  }

  const inputStyle: React.CSSProperties = { padding: '8px 10px', border: '1px solid #cbd5f5', borderRadius: 10, width: 120 }

  return (
    <div data-testid="edit-farm-projection-settings" style={{ marginTop: 12, display: 'grid', gap: 8 }}>
      <strong>{t('editFarm.projection.title')}</strong>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>
          <span>{t('editFarm.projection.target')}</span>
          <input
            data-testid="edit-farm-projection-target"
            inputMode="decimal"
            value={target}
            onChange={(event) => setTarget(event.target.value)}
            disabled={disabled}
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>
          <span>{t('editFarm.projection.tgc')}</span>
          <input
            data-testid="edit-farm-projection-tgc"
            inputMode="decimal"
            value={tgc}
            onChange={(event) => setTgc(event.target.value)}
            disabled={disabled}
            style={inputStyle}
          />
        </label>
        <GradientButton
          size="xs"
          variant="success"
          onClick={() => void save()}
          disabled={disabled || saving || !dirty}
          data-testid="edit-farm-projection-save"
        >
          {t('common.save')}
        </GradientButton>
      </div>
      <span style={{ fontSize: 12, color: '#6b7280' }}>{t('editFarm.projection.help')}</span>
      {error ? <div role="alert" style={{ color: '#b91c1c', fontWeight: 700, fontSize: 13 }}>{error}</div> : null}
    </div>
  )
}
